import { Download } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { clock, dayLabel } from '../lib/format';
import type { SignInLog } from '../lib/types';
import { useParent } from './ParentContext';

const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).getTime() : undefined);
const dayEnd = (d: string) => (d ? new Date(`${d}T23:59:59.999`).getTime() : undefined);
const RESULT_TEXT: Record<string, string> = { ok: 'Signed in', failed: 'Failed', blocked: 'Blocked' };

/** One CSV cell, quoted when needed. */
const cell = (v: string | number) => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Admin → Audit log → Sign-ins (#429): every sign-in by parents and students, successful or not. Filter by person,
 * result and dates, search, and download what's shown as a CSV report.
 */
export function SignInActivity() {
  const { toast } = useParent();
  const [data, setData] = useState<SignInLog | null>(null);
  const [who, setWho] = useState('');
  const [kind, setKind] = useState('');
  const [result, setResult] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const params = useCallback(
    (limit: number) => {
      const p = new URLSearchParams();
      if (who) p.set('who', who);
      if (kind) p.set('kind', kind);
      if (result) p.set('result', result);
      const f = dayStart(from);
      const e = dayEnd(to);
      if (f) p.set('from', String(f));
      if (e) p.set('to', String(e));
      if (query) p.set('q', query);
      p.set('limit', String(limit));
      return p.toString();
    },
    [who, kind, result, from, to, query],
  );

  useEffect(() => {
    api.get<SignInLog>(`/api/admin/sign-ins?${params(500)}`).then(setData).catch((err) => toast({ text: (err as Error).message, tone: 'alert' }));
  }, [params, toast]);

  const filtered = !!(who || kind || result || from || to || query);
  const reset = () => {
    setWho('');
    setKind('');
    setResult('');
    setFrom('');
    setTo('');
    setQ('');
  };

  /** Download every matching sign-in (not just the 500 on screen) as a CSV report. */
  async function download() {
    try {
      const all = await api.get<SignInLog>(`/api/admin/sign-ins?${params(10000)}`);
      const rows = [
        ['Date', 'Time', 'Who', 'Sign-in', 'Type', 'Result', 'How', 'Reason', 'Device', 'IP address'],
        ...all.entries.map((r) => [
          new Date(r.ts).toLocaleDateString(),
          new Date(r.ts).toLocaleTimeString(),
          r.name,
          r.login,
          r.kind === 'student' ? 'Student' : 'Parent',
          RESULT_TEXT[r.result] ?? r.result,
          r.method,
          r.reason,
          r.device,
          r.ip,
        ]),
      ];
      const csv = rows.map((r) => r.map(cell).join(',')).join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      a.download = `classguardian-sign-ins-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  const s = data?.summary;
  return (
    <div className="stack">
      <section className="card stack audit-filters">
        <div className="form-row">
          <label className="grow">
            Who
            <select value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="">Anyone</option>
              {(data?.people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.kind === 'student' ? ' · student' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="grow">
            Result
            <select value={result} onChange={(e) => setResult(e.target.value)}>
              <option value="">Any result</option>
              <option value="ok">Signed in</option>
              <option value="unsuccessful">Failed or blocked</option>
              <option value="failed">Failed</option>
              <option value="blocked">Blocked</option>
            </select>
          </label>
          <label className="grow">
            Type
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Parents and students</option>
              <option value="parent">Parents</option>
              <option value="student">Students</option>
            </select>
          </label>
        </div>
        <div className="form-row">
          <label className="grow">
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined} />
          </label>
          <label className="grow">
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined} />
          </label>
        </div>
        <label>
          Search
          <input placeholder="Search names, emails, devices or IP addresses" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="row-between">
          {filtered ? (
            <button className="btn small" onClick={reset}>
              Clear filters
            </button>
          ) : (
            <span />
          )}
          <button className="btn small" onClick={download} disabled={!s?.total}>
            <Download size={15} /> Download report (CSV)
          </button>
        </div>
      </section>

      {s && (
        <div className="usage-tiles">
          <div className="usage-tile">
            <span className="usage-value">{s.ok}</span>
            <span className="muted small">Sign-ins</span>
          </div>
          <div className={`usage-tile ${s.unsuccessful ? 'warn' : ''}`}>
            <span className="usage-value">{s.unsuccessful}</span>
            <span className="muted small">Failed or blocked</span>
          </div>
          <div className="usage-tile">
            <span className="usage-value">{s.people}</span>
            <span className="muted small">People signed in</span>
          </div>
        </div>
      )}

      {!data ? (
        <p className="muted">Loading…</p>
      ) : !data.entries.length ? (
        <p className="muted">{filtered ? 'No matching sign-ins.' : 'No sign-ins recorded yet.'}</p>
      ) : (
        <section className="card">
          <ul className="admin-audit">
            {data.entries.map((r) => (
              <li key={r.id}>
                <div className="audit-title">
                  {r.result !== 'ok' && <span className="tag bug">{RESULT_TEXT[r.result]}</span>} {r.name || r.login}
                  {r.result === 'ok' ? ' signed in' : ''}
                  {r.kind === 'student' && <span className="muted small"> · student</span>}
                </div>
                <ul className="audit-lines">
                  <li>
                    {r.method.charAt(0).toUpperCase() + r.method.slice(1)}
                    {r.reason ? ` · ${r.reason}` : ''}
                  </li>
                  {(r.device || r.ip) && <li>{[r.device, r.ip].filter(Boolean).join(' · ')}</li>}
                </ul>
                <div className="muted small">
                  {r.name ? `${r.login} · ` : ''}
                  {dayLabel(r.ts)} {clock(r.ts)}
                </div>
              </li>
            ))}
          </ul>
          {data.entries.length >= 500 && <p className="muted small">Showing the latest 500. Download the report for all of them.</p>}
        </section>
      )}
      <p className="muted small">Sign-ins are kept for {data?.keepDays ?? 365} days (Server settings → Monitoring). Each parent’s total on Families is kept for good.</p>
    </div>
  );
}
